import React, { useEffect, useState } from "react";

const ReviewLoanDetails = ({
  isReviewLoanDetailsOpen,
  closeReviewLoanDetails,
  handleToGuarantors,
  handleToApplicationSuccess,
  loanProductName,
  handleReviewBack,
  onCancel,
  loanDetails,
}) => {
  const [amount, setAmount] = useState(0);
  const [interest, setInterest] = useState(0);
  const [duration, setDuration] = useState(0);
  const [totalRepayment, setTotalRepayment] = useState(0);
  const [monthlyRepayment, setMonthlyRepayment] = useState(0);
  const [repaymentInterval, setRepaymentInterval] = useState("Monthly");

  useEffect(() => {
    if (!loanDetails) return;

    setAmount(loanDetails.amount || 0);
    setInterest(loanDetails.interest || 0);
    setDuration(loanDetails.duration || "");
    setRepaymentInterval(loanDetails.repaymentInterval || "Monthly");
    setTotalRepayment(loanDetails.totalRepayment || 0);
    setMonthlyRepayment(loanDetails.monthlyRepayment || 0);
  }, [loanDetails]);

  if (!isReviewLoanDetailsOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4 backdrop-blur-sm overflow-y-auto">
      {/* Retained 750px desktop max-width structure with responsive max-height */}
      <div className="relative bg-white shadow-xl rounded-md w-full max-w-[750px] my-auto flex flex-col max-h-[90vh] transition-all">
        {/* Header */}
        <div className="flex justify-between items-center p-4 sm:p-6 border-b border-gray-200 shrink-0">
          <h2 className="text-xl sm:text-2xl font-bold text-black break-words">
            Loan Summary
          </h2>
          <button
            onClick={closeReviewLoanDetails}
            className="text-gray-500 hover:text-gray-800 text-xl font-bold leading-none p-1 shrink-0"
            aria-label="Close modal"
          >
            ✕
          </button>
        </div>

        {/* Scrollable Body Container */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 md:p-8">
          <p className="text-gray-600 mb-6 text-xs sm:text-sm">
            Review your loan details before proceeding.
          </p>

          <h3 className="text-xs sm:text-sm font-semibold text-gray-900 mb-3">
            Repayment Summary
          </h3>

          {/* Repayment Summary Grid / Card */}
          <div className="bg-[#dcecff] p-4 sm:p-6 rounded-md grid grid-cols-1 sm:grid-cols-2 gap-y-3 sm:gap-y-4 gap-x-6 text-xs sm:text-[15px]">
            <div className="flex justify-between sm:block border-b sm:border-b-0 border-blue-200/60 pb-1 sm:pb-0">
              <span className="text-gray-800 font-medium">Loan Product</span>
              <span className="text-gray-900 font-semibold sm:hidden text-right break-all">
                {loanProductName}
              </span>
            </div>
            <div className="hidden sm:block text-gray-900 font-semibold text-right break-all">
              {loanProductName}
            </div>

            <div className="flex justify-between sm:block border-b sm:border-b-0 border-blue-200/60 pb-1 sm:pb-0">
              <span className="text-gray-800 font-medium">Loan Amount</span>
              <span className="text-gray-900 font-semibold sm:hidden text-right break-all">
                KES {Number(amount).toLocaleString()}
              </span>
            </div>
            <div className="hidden sm:block text-gray-900 font-semibold text-right break-all">
              KES {Number(amount).toLocaleString()}
            </div>

            <div className="flex justify-between sm:block border-b sm:border-b-0 border-blue-200/60 pb-1 sm:pb-0">
              <span className="text-gray-800 font-medium">
                Loan Interest (%)
              </span>
              <span className="text-gray-900 font-semibold sm:hidden text-right break-all">
                {interest}%
              </span>
            </div>
            <div className="hidden sm:block text-gray-900 font-semibold text-right break-all">
              {interest}%
            </div>

            <div className="flex justify-between sm:block border-b sm:border-b-0 border-blue-200/60 pb-1 sm:pb-0">
              <span className="text-gray-800 font-medium">Total Repayment</span>
              <span className="text-gray-900 font-semibold sm:hidden text-right break-all">
                KES {Number(totalRepayment).toLocaleString()} ({duration})
              </span>
            </div>
            <div className="hidden sm:block text-gray-900 font-semibold text-right break-all">
              KES {Number(totalRepayment).toLocaleString()} ({duration})
            </div>

            <div className="flex justify-between sm:block">
              <span className="text-gray-800 font-medium">Repayments</span>
              <span className="text-gray-900 font-semibold sm:hidden text-right break-all">
                KES {Number(monthlyRepayment).toLocaleString()} / month (
                {repaymentInterval})
              </span>
            </div>
            <div className="hidden sm:block text-gray-900 font-semibold text-right break-all">
              KES {Number(monthlyRepayment).toLocaleString()} / month (
              {repaymentInterval})
            </div>
          </div>
        </div>

        {/* Action Footer */}
        <div className="flex flex-col-reverse sm:flex-row justify-between items-center border-t border-gray-200 p-4 sm:p-6 gap-3 shrink-0">
          <div className="flex flex-col sm:flex-row w-full sm:w-auto gap-2 sm:gap-3">
            <button
              onClick={handleReviewBack}
              className="w-full sm:w-auto border border-[#074073] text-[#074073] hover:bg-[#f5f9ff] font-medium py-2 px-4 rounded-md text-xs sm:text-sm transition-colors duration-150"
            >
              Back
            </button>
            <button
              onClick={onCancel || closeReviewLoanDetails}
              className="w-full sm:w-auto border border-[#074073] text-[#074073] hover:bg-[#f5f9ff] font-medium py-2 px-4 rounded-md text-xs sm:text-sm transition-colors duration-150"
            >
              Cancel Application
            </button>
          </div>

          {loanProductName === "Emergency Loan" ? (
            <button
              onClick={handleToApplicationSuccess}
              className="w-full sm:w-auto sm:min-w-[220px] h-10 bg-[#074073] hover:bg-[#052f5a] text-white font-medium py-2 px-5 rounded-md text-xs sm:text-sm transition-colors duration-150 whitespace-nowrap"
            >
              Finish Application
            </button>
          ) : (
            <button
              onClick={handleToGuarantors}
              className="w-full sm:w-auto sm:min-w-[220px] h-10 bg-[#074073] hover:bg-[#052f5a] text-white font-medium py-2 px-5 rounded-md text-xs sm:text-sm transition-colors duration-150 whitespace-nowrap"
            >
              Proceed to Add Guarantors
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReviewLoanDetails;
